import _classCallCheck from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/createClass";
import _get from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/get";
import _inherits from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/inherits";
import _possibleConstructorReturn from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/getPrototypeOf";

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

import { _optionalChain } from '@sentry/utils/esm/buildPolyfills';
import { BaseClient, SDK_VERSION, getEnvelopeEndpointWithUrlEncodedAuth } from '@sentry/core';
import { logger, createClientReportEnvelope, dsnToString, serializeEnvelope } from '@sentry/utils';
import { eventFromException as _eventFromException, eventFromMessage as _eventFromMessage } from './eventbuilder.js';
import { WINDOW } from './helpers.js';
import { BREADCRUMB_INTEGRATION_ID } from './integrations/breadcrumbs.js';
/**
 * Configuration options for the Sentry Browser SDK.
 * @see @sentry/types Options for more information.
 */

/**
 * The Sentry Browser SDK Client.
 *
 * @see BrowserOptions for documentation on configuration options.
 * @see SentryClient for usage documentation.
 */

var BrowserClient = /*#__PURE__*/function (_BaseClient) {
  _inherits(BrowserClient, _BaseClient);

  var _super = _createSuper(BrowserClient);

  /**
   * Creates a new Browser SDK instance.
   *
   * @param options Configuration options for this SDK.
   */
  function BrowserClient(options) {
    var _this;

    _classCallCheck(this, BrowserClient);

    options._metadata = options._metadata || {};
    options._metadata.sdk = options._metadata.sdk || {
      name: 'sentry.javascript.browser',
      packages: [{
        name: 'npm:@sentry/browser',
        version: SDK_VERSION
      }],
      version: SDK_VERSION
    };
    _this = _super.call(this, options);

    if (options.sendClientReports && WINDOW.document) {
      WINDOW.document.addEventListener('visibilitychange', function () {
        if (WINDOW.document.visibilityState === 'hidden') {
          _this._flushOutcomes();
        }
      });
    }

    return _this;
  }
  /**
   * @inheritDoc
   */


  _createClass(BrowserClient, [{
    key: "eventFromException",
    value: function eventFromException(exception, hint) {
      return _eventFromException(this._options.stackParser, exception, hint, this._options.attachStacktrace);
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "eventFromMessage",
    value: function eventFromMessage(message) {
      var level = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'info';
      var hint = arguments.length > 2 ? arguments[2] : undefined;
      return _eventFromMessage(this._options.stackParser, message, level, hint, this._options.attachStacktrace);
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "sendEvent",
    value: function sendEvent(event, hint) {
      // We only want to add the sentry event breadcrumb when the user has the breadcrumb integration installed and
      // activated its `sentry` option.
      // We also do not want to use the `Breadcrumbs` class here directly, because we do not want it to be included in
      // bundles, if it is not used by the SDK.
      // This all sadly is a bit ugly, but we currently don't have a "pre-send" hook on the integrations so we do it this
      // way for now.
      var breadcrumbIntegration = this.getIntegrationById(BREADCRUMB_INTEGRATION_ID); // We check for definedness of `addSentryBreadcrumb` in case users provided their own integration with id
      // "Breadcrumbs" that does not have this function.

      _optionalChain([breadcrumbIntegration, 'optionalAccess', function (_) {
        return _.addSentryBreadcrumb;
      }, 'optionalCall', function (_2) {
        return _2(event);
      }]);

      _get(_getPrototypeOf(BrowserClient.prototype), "sendEvent", this).call(this, event, hint);
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "_prepareEvent",
    value: function _prepareEvent(event, hint, scope) {
      event.platform = event.platform || 'javascript';
      return _get(_getPrototypeOf(BrowserClient.prototype), "_prepareEvent", this).call(this, event, hint, scope);
    }
    /**
     * Sends client reports as an envelope.
     */

  }, {
    key: "_flushOutcomes",
    value: function _flushOutcomes() {
      var outcomes = this._clearOutcomes();

      if (outcomes.length === 0) {
        (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log('No outcomes to send');
        return;
      }

      if (!this._dsn) {
        (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log('No dsn provided, will not send outcomes');
        return;
      }

      (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log('Sending outcomes:', outcomes);
      var url = getEnvelopeEndpointWithUrlEncodedAuth(this._dsn, this._options);
      var envelope = createClientReportEnvelope(outcomes, this._options.tunnel && dsnToString(this._dsn));

      try {
        var isRealNavigator = Object.prototype.toString.call(WINDOW && WINDOW.navigator) === '[object Navigator]';
        var hasSendBeacon = isRealNavigator && typeof WINDOW.navigator.sendBeacon === 'function'; // Make sure beacon is not used if user configures custom transport options

        if (hasSendBeacon && !this._options.transportOptions) {
          // Prevent illegal invocations - https://xgwang.me/posts/you-may-not-know-beacon/#it-may-throw-error%2C-be-sure-to-catch
          var sendBeacon = WINDOW.navigator.sendBeacon.bind(WINDOW.navigator);
          sendBeacon(url, serializeEnvelope(envelope));
        } else {
          // If beacon is not supported or if they are using the tunnel option
          // use our regular transport to send client reports to Sentry.
          this._sendEnvelope(envelope);
        }
      } catch (e) {
        (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.error(e);
      }
    }
  }]);

  return BrowserClient;
}(BaseClient);

export { BrowserClient };